import { useCallback, useEffect, useRef, useState, type ChangeEvent, type CompositionEvent, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent } from 'react'
import { AppDialog, LmsButton } from '@shared/ui'
import {
  EMPTY_FLIPBOOK_ANNOTATION_HISTORY,
  EMPTY_FLIPBOOK_ANNOTATION_STATE,
  type FlipbookAnnotationHistoryControls,
  type FlipbookAnnotationState,
  type FlipbookAnnotationStroke,
  type FlipbookNormalizedPoint,
  type FlipbookTextAlignment,
  type FlipbookTextAnnotation,
} from './flipbook-annotation-contracts'
import { getFlipbookPerformanceDiagnostics, useFlipbookPerformanceRenderCount } from './flipbook-performance-diagnostics'
import {
  acceptsTextInsertion,
  FLIPBOOK_TEXT_HANDLE_SIZE,
  FLIPBOOK_TEXT_MAX_LENGTH,
  getTextFrameLayout,
  getTextGestureFrame,
  measureTextContent,
  type TextFrame,
  type TextFrameGesture,
} from './flipbook-text-geometry'

export type FlipbookAnnotationTool = 'none' | 'pen' | 'highlighter' | 'text' | 'eraser'
export type FlipbookAnnotationColorTool = Extract<FlipbookAnnotationTool, 'pen' | 'highlighter' | 'text'>

type NormalizedPoint = FlipbookNormalizedPoint
type AnnotationStroke = FlipbookAnnotationStroke
type TextAnnotation = FlipbookTextAnnotation
type AnnotationState = FlipbookAnnotationState

interface FlipbookAnnotationLayerProps {
  readonly activeTool: FlipbookAnnotationTool
  readonly disabled: boolean
  readonly highlighterColor: string
  readonly hydratedState: FlipbookAnnotationState | null
  readonly isHydrationReady: boolean
  readonly isSpreadView: boolean
  readonly hydrationKey: string
  readonly onHistoryChange: (controls: FlipbookAnnotationHistoryControls) => void
  readonly onStateChange: (state: FlipbookAnnotationState) => void
  readonly penColor: string
  readonly textColor: string
  readonly visiblePageNumbers: readonly number[]
}

const MAX_POINTS_PER_STROKE = 600
const TEXT_BOX_WIDTH = 0.3
const TEXT_BOX_HEIGHT = 0.08
const TEXT_DRAG_THRESHOLD_PX = 3
const DEFAULT_TEXT_ALIGNMENT: FlipbookTextAlignment = 'left'
type AnnotationLayerOperation = 'backward' | 'forward' | 'front' | 'back'
const EMPTY_ANNOTATION_STATE = EMPTY_FLIPBOOK_ANNOTATION_STATE

function clamp(value: number) {
  return Math.min(1, Math.max(0, value))
}

function clampPosition(value: number, size: number) {
  return Math.min(1 - size, Math.max(0, value))
}

function getNormalizedPoint(event: PointerEvent<SVGSVGElement>): NormalizedPoint {
  const bounds = event.currentTarget.getBoundingClientRect()

  return {
    x: clamp((event.clientX - bounds.left) / bounds.width),
    y: clamp((event.clientY - bounds.top) / bounds.height),
  }
}

function getStrokePoints(points: readonly NormalizedPoint[]) {
  return points.map(({ x, y }) => `${x},${y}`).join(' ')
}

function getAnnotationPage(element: Element) {
  return element.closest('.pdfFlipbookAnnotationPage') as HTMLElement | null
}

type TextLayout = ReturnType<typeof getTextFrameLayout> & { readonly pageWidth: number; readonly pageHeight: number }

function hasAnnotations(state: AnnotationState) {
  return Object.values(state.strokesByPage).some((annotations) => annotations.length > 0)
    || Object.values(state.textsByPage).some((annotations) => annotations.length > 0)
}

function getHighestLayer(state: AnnotationState) {
  const layers = [
    ...Object.values(state.strokesByPage).flat().map((stroke) => stroke.layer),
    ...Object.values(state.textsByPage).flat().map((text) => text.layer),
  ]
  return layers.length > 0 ? Math.max(...layers) : 0
}

export function FlipbookAnnotationLayer({
  activeTool,
  disabled,
  highlighterColor,
  hydratedState,
  isHydrationReady,
  isSpreadView,
  hydrationKey,
  onHistoryChange,
  onStateChange,
  penColor,
  textColor,
  visiblePageNumbers,
}: FlipbookAnnotationLayerProps) {
  useFlipbookPerformanceRenderCount('annotation')
  const [annotationState, setAnnotationState] = useState<AnnotationState>(EMPTY_ANNOTATION_STATE)
  const [history, setHistory] = useState<readonly AnnotationState[]>([])
  const [redoHistory, setRedoHistory] = useState<readonly AnnotationState[]>([])
  const [draftStroke, setDraftStroke] = useState<AnnotationStroke | null>(null)
  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false)
  const [activeTextId, setActiveTextId] = useState<string | null>(null)
  const [selectedStrokeId, setSelectedStrokeId] = useState<string | null>(null)
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null)
  const [draggingTextId, setDraggingTextId] = useState<string | null>(null)
  const [textLayouts, setTextLayouts] = useState<Record<string, TextLayout>>({})
  const [textDrafts, setTextDrafts] = useState<Record<string, string>>({})
  const [textNotices, setTextNotices] = useState<Record<string, string>>({})
  const [textGesturePreview, setTextGesturePreview] = useState<{ readonly id: string; readonly layout: TextLayout } | null>(null)
  const layerRef = useRef<HTMLDivElement | null>(null)
  const textDraftsRef = useRef<Record<string, string>>({})
  const composingTextRef = useRef<{ readonly id: string; readonly previous: string } | null>(null)
  const pointerIdRef = useRef<number | null>(null)
  const draftStrokeRef = useRef<AnnotationStroke | null>(null)
  const draftPointsStringRef = useRef('')
  const draftPolylineRef = useRef<SVGPolylineElement | null>(null)
  const layerOrderRef = useRef(0)
  const strokeIdRef = useRef(0)
  const textIdRef = useRef(0)
  useEffect(() => {
    if (!selectedTextId || (!textNotices[selectedTextId] && !textLayouts[selectedTextId]?.recovered)) return
    const frame = requestAnimationFrame(() => {
      textRefs.current[selectedTextId]?.parentElement?.querySelector('.pdfFlipbookTextNotice')?.scrollIntoView({ block: 'nearest' })
    })
    return () => { cancelAnimationFrame(frame) }
  }, [selectedTextId, textNotices, textLayouts])
  const textRefs = useRef<Record<string, HTMLTextAreaElement | null>>({})
  const annotationStateRef = useRef(annotationState)
  const hydratedKeyRef = useRef<string | null>(null)
  const textEditRef = useRef<{ readonly id: string; readonly initialState: AnnotationState; readonly isNew: boolean } | null>(null)
  const textDragRef = useRef<{
    id: string
    pointerId: number
    startClientX: number
    startClientY: number
    startRenderedWidth: number
    startRenderedHeight: number
    initialFrame: TextFrame
    initialState: AnnotationState
    gesture: TextFrameGesture
    target: HTMLButtonElement
    layout: TextLayout
    moved: boolean
  } | null>(null)

  const setTextDraft = (id: string, value: string) => {
    textDraftsRef.current = { ...textDraftsRef.current, [id]: value }
    setTextDrafts(textDraftsRef.current)
  }

  const clearTextDraft = (id: string) => {
    const next = { ...textDraftsRef.current }
    delete next[id]
    textDraftsRef.current = next
    setTextDrafts(next)
    setTextNotices((current) => ({ ...current, [id]: '' }))
  }

  const measureTextLayout = useCallback((annotation: TextAnnotation, frame: TextFrame = annotation): TextLayout | null => {
    const textarea = textRefs.current[annotation.id]
    const page = textarea ? getAnnotationPage(textarea) : null
    if (!textarea || !page?.clientWidth || !page.clientHeight) {
      return null
    }
    const text = textDraftsRef.current[annotation.id] ?? annotation.text
    return {
      ...getTextFrameLayout(frame, { width: page.clientWidth, height: page.clientHeight }, measureTextContent(textarea, page, frame.width, text)),
      pageWidth: page.clientWidth,
      pageHeight: page.clientHeight,
    }
  }, [])

  const remeasureTextLayouts = useCallback(() => {
    const layouts: Record<string, TextLayout> = {}
    for (const annotation of Object.values(annotationStateRef.current.textsByPage).flat()) {
      const layout = measureTextLayout(annotation)
      if (layout) layouts[annotation.id] = layout
    }
    setTextLayouts((current) => JSON.stringify(current) === JSON.stringify(layouts) ? current : layouts)
  }, [measureTextLayout])

  const resetTextDrafts = useCallback(() => {
    textDraftsRef.current = {}
    composingTextRef.current = null
    setTextDrafts({})
    setTextNotices({})
  }, [])

  const cancelTextGesture = useCallback(() => {
    const drag = textDragRef.current
    textDragRef.current = null
    if (drag) {
      getFlipbookPerformanceDiagnostics().endPointer('text')
      if (drag.target.hasPointerCapture(drag.pointerId)) drag.target.releasePointerCapture(drag.pointerId)
    }
    setDraggingTextId(null)
    setTextGesturePreview(null)
  }, [])

  const replaceAnnotationState = useCallback((next: AnnotationState, notify = true) => {
    annotationStateRef.current = next
    setAnnotationState(next)
    if (notify) {
      onStateChange(next)
    }
  }, [onStateChange])

  const recordAnnotationAction = useCallback((next: AnnotationState, previous = annotationStateRef.current) => {
    replaceAnnotationState(next)
    setHistory((current) => [...current, previous])
    setRedoHistory([])
  }, [replaceAnnotationState])

  const undoAnnotationAction = useCallback(() => {
    const previous = history[history.length - 1]
    if (!previous) {
      return
    }

    const current = annotationStateRef.current
    cancelTextGesture()
    resetTextDrafts()
    replaceAnnotationState(previous)
    setHistory((items) => items.slice(0, -1))
    setRedoHistory((items) => [...items, current])
    setActiveTextId(null)
    setSelectedStrokeId(null)
    setSelectedTextId(null)
    textEditRef.current = null
  }, [cancelTextGesture, history, replaceAnnotationState, resetTextDrafts])

  const redoAnnotationAction = useCallback(() => {
    const next = redoHistory[redoHistory.length - 1]
    if (!next) {
      return
    }

    const current = annotationStateRef.current
    cancelTextGesture()
    resetTextDrafts()
    replaceAnnotationState(next)
    setRedoHistory((items) => items.slice(0, -1))
    setHistory((items) => [...items, current])
    setActiveTextId(null)
    setSelectedStrokeId(null)
    setSelectedTextId(null)
    textEditRef.current = null
  }, [cancelTextGesture, redoHistory, replaceAnnotationState, resetTextDrafts])

  const clearAnnotations = useCallback(() => {
    if (disabled || !hasAnnotations(annotationStateRef.current)) {
      return
    }

    setIsClearDialogOpen(true)
  }, [disabled])

  const confirmClearAnnotations = useCallback(() => {
    if (!hasAnnotations(annotationStateRef.current)) {
      setIsClearDialogOpen(false)
      return
    }

    recordAnnotationAction(EMPTY_ANNOTATION_STATE)
    cancelTextGesture()
    resetTextDrafts()
    setActiveTextId(null)
    setSelectedStrokeId(null)
    setSelectedTextId(null)
    textEditRef.current = null
    setIsClearDialogOpen(false)
  }, [cancelTextGesture, recordAnnotationAction, resetTextDrafts])

  useEffect(() => {
    if (!isHydrationReady || !hydratedState || hydratedKeyRef.current === hydrationKey) {
      return
    }

    hydratedKeyRef.current = hydrationKey
    cancelTextGesture()
    resetTextDrafts()
    replaceAnnotationState(hydratedState, false)
    setHistory([])
    setRedoHistory([])
    setActiveTextId(null)
    setSelectedStrokeId(null)
    setSelectedTextId(null)
    textEditRef.current = null
  }, [cancelTextGesture, hydratedState, hydrationKey, isHydrationReady, replaceAnnotationState, resetTextDrafts])

  useEffect(() => {
    const frame = window.requestAnimationFrame(remeasureTextLayouts)
    return () => { window.cancelAnimationFrame(frame) }
  }, [annotationState, textDrafts, remeasureTextLayouts])

  useEffect(() => {
    let frame = 0
    let mounted = true
    const schedule = () => {
      if (!mounted) return
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(remeasureTextLayouts)
    }
    const observer = new ResizeObserver(schedule)
    layerRef.current?.querySelectorAll('.pdfFlipbookAnnotationPage').forEach((page) => { observer.observe(page) })
    document.fonts.addEventListener('loadingdone', schedule)
    void document.fonts.ready.then(schedule)
    schedule()
    return () => {
      mounted = false
      window.cancelAnimationFrame(frame)
      observer.disconnect()
      document.fonts.removeEventListener('loadingdone', schedule)
    }
  }, [isSpreadView, remeasureTextLayouts, visiblePageNumbers])

  useEffect(() => {
    if (disabled || !visiblePageNumbers.some((page) => annotationStateRef.current.textsByPage[page]?.some((text) => text.id === textDragRef.current?.id))) {
      cancelTextGesture()
    }
  }, [cancelTextGesture, disabled, visiblePageNumbers])

  useEffect(() => () => { onHistoryChange(EMPTY_FLIPBOOK_ANNOTATION_HISTORY) }, [onHistoryChange])

  useEffect(() => {
    if (!activeTextId) {
      return
    }

    textRefs.current[activeTextId]?.focus()
  }, [activeTextId])

  const beginStroke = (event: PointerEvent<SVGSVGElement>, page: number) => {
    if (disabled || activeTool === 'none' || event.button !== 0 || !event.isPrimary) {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (activeTool === 'text') {
      if (composingTextRef.current) return
      if (activeTextId) commitText(activeTextId)
      textIdRef.current += 1
      layerOrderRef.current += 1
      const point = getNormalizedPoint(event)
      const pageElement = getAnnotationPage(event.currentTarget)
      const width = Math.max(TEXT_BOX_WIDTH, Math.min(1, FLIPBOOK_TEXT_HANDLE_SIZE * 2 / (pageElement?.clientWidth || 1)))
      const textAnnotation: TextAnnotation = {
        align: DEFAULT_TEXT_ALIGNMENT,
        id: `text-annotation-${textIdRef.current}`,
        layer: layerOrderRef.current,
        page,
        text: '',
        color: textColor,
        x: clampPosition(point.x, width),
        y: clampPosition(point.y, TEXT_BOX_HEIGHT),
        width,
        height: TEXT_BOX_HEIGHT,
      }

      const current = annotationStateRef.current
      recordAnnotationAction({
        ...current,
        textsByPage: {
          ...current.textsByPage,
          [page]: [...(current.textsByPage[page] ?? []), textAnnotation],
        },
      })
      textEditRef.current = { id: textAnnotation.id, initialState: current, isNew: true }
      setSelectedStrokeId(null)
      setSelectedTextId(textAnnotation.id)
      setActiveTextId(textAnnotation.id)
      return
    }

    if (activeTool === 'eraser') {
      return
    }

    setActiveTextId(null)
    setSelectedStrokeId(null)
    setSelectedTextId(null)
    event.currentTarget.setPointerCapture(event.pointerId)
    pointerIdRef.current = event.pointerId
    getFlipbookPerformanceDiagnostics().markPointerDown('draw')
    strokeIdRef.current += 1
    layerOrderRef.current += 1
    const nextDraftStroke = {
      id: `annotation-${strokeIdRef.current}`,
      layer: layerOrderRef.current,
      page,
      points: [getNormalizedPoint(event)],
      tool: activeTool,
      color: activeTool === 'highlighter' ? highlighterColor : penColor,
    }
    draftStrokeRef.current = nextDraftStroke
    draftPointsStringRef.current = getStrokePoints(nextDraftStroke.points)
    setDraftStroke(nextDraftStroke)
  }

  const continueStroke = (event: PointerEvent<SVGSVGElement>) => {
    const currentDraftStroke = draftStrokeRef.current
    if (!currentDraftStroke || pointerIdRef.current !== event.pointerId) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    getFlipbookPerformanceDiagnostics().markPointerMove('draw')
    const point = getNormalizedPoint(event)
    if (currentDraftStroke.points.length >= MAX_POINTS_PER_STROKE) {
      return
    }

    const nextDraftStroke = {
      ...currentDraftStroke,
      points: [...currentDraftStroke.points, point],
    }
    draftStrokeRef.current = nextDraftStroke
    draftPointsStringRef.current = `${draftPointsStringRef.current} ${point.x},${point.y}`
    draftPolylineRef.current?.setAttribute('points', draftPointsStringRef.current)
  }

  const finishStroke = (event: PointerEvent<SVGSVGElement>, cancelled = false) => {
    const currentDraftStroke = draftStrokeRef.current
    if (!currentDraftStroke || pointerIdRef.current !== event.pointerId) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    getFlipbookPerformanceDiagnostics().endPointer('draw')
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    if (!cancelled && currentDraftStroke.points.length >= 2) {
      const current = annotationStateRef.current
      recordAnnotationAction({
        ...current,
        strokesByPage: {
          ...current.strokesByPage,
          [currentDraftStroke.page]: [...(current.strokesByPage[currentDraftStroke.page] ?? []), currentDraftStroke],
        },
      })
    }

    pointerIdRef.current = null
    draftStrokeRef.current = null
    draftPointsStringRef.current = ''
    draftPolylineRef.current = null
    setDraftStroke(null)
  }

  const updateText = (id: string, text: string, textarea: HTMLTextAreaElement) => {
    const previous = textDraftsRef.current[id] ?? Object.values(annotationStateRef.current.textsByPage).flat().find((item) => item.id === id)?.text ?? ''
    if (text.length > FLIPBOOK_TEXT_MAX_LENGTH) {
      textarea.value = previous
      setTextNotices((current) => ({ ...current, [id]: 'Tối đa 240 ký tự. Nội dung vừa nhập chưa được thêm.' }))
      return false
    }
    setTextDraft(id, text)
    const current = annotationStateRef.current
    const annotation = Object.values(current.textsByPage).flat().find((item) => item.id === id)
    const layout = annotation ? measureTextLayout(annotation) : null
    if (!layout?.fits) {
      setTextNotices((current) => ({ ...current, [id]: 'Chữ chưa vừa trang, chưa lưu thay đổi. Kéo rộng khung hoặc rút ngắn nội dung.' }))
      return false
    }
    setTextNotices((current) => ({ ...current, [id]: '' }))
    const next: AnnotationState = {
      ...current,
      textsByPage: Object.fromEntries(
        Object.entries(current.textsByPage).map(([page, annotations]) => [
        page,
        annotations.map((annotation) => annotation.id === id
          ? {
            ...annotation,
            ...layout.frame,
            text,
          }
          : annotation),
        ]),
      ),
    }
    replaceAnnotationState(next)
    return true
  }

  const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>, annotation: TextAnnotation) => {
    if (composingTextRef.current?.id === annotation.id || (event.nativeEvent as InputEvent).isComposing) {
      setTextDraft(annotation.id, event.currentTarget.value)
    } else updateText(annotation.id, event.currentTarget.value, event.currentTarget)
  }

  const beginTextComposition = (annotation: TextAnnotation) => {
    composingTextRef.current = { id: annotation.id, previous: textDraftsRef.current[annotation.id] ?? annotation.text }
  }

  const finishTextComposition = (event: CompositionEvent<HTMLTextAreaElement>, annotation: TextAnnotation) => {
    const previous = composingTextRef.current?.previous ?? annotation.text
    composingTextRef.current = null
    if (event.currentTarget.value.length > FLIPBOOK_TEXT_MAX_LENGTH) {
      event.currentTarget.value = previous
      setTextDraft(annotation.id, previous)
      setTextNotices((current) => ({ ...current, [annotation.id]: 'Tối đa 240 ký tự. Nội dung vừa nhập chưa được thêm.' }))
    } else updateText(annotation.id, event.currentTarget.value, event.currentTarget)
  }

  const commitText = (id: string) => {
    const current = annotationStateRef.current
    const textAnnotation = Object.values(current.textsByPage).flat().find((annotation) => annotation.id === id)
    const draft = textDraftsRef.current[id]
    if (draft !== undefined && draft !== textAnnotation?.text) {
      setActiveTextId((current) => current === id ? null : current)
      return false
    }
    if (!textAnnotation || textAnnotation.text.trim().length === 0) {
      cancelText(id)
      return false
    }

    const edit = textEditRef.current
    if (edit?.id === id && !edit.isNew && edit.initialState !== current) {
      setHistory((items) => [...items, edit.initialState])
      setRedoHistory([])
    }
    textEditRef.current = null
    clearTextDraft(id)
    setActiveTextId((current) => current === id ? null : current)
    return true
  }

  const recolorSelectedText = useCallback((color: string) => {
    if (!selectedTextId) {
      return
    }

    const current = annotationStateRef.current
    const selectedText = Object.values(current.textsByPage).flat().find((annotation) => annotation.id === selectedTextId)
    if (!selectedText || selectedText.color === color) {
      return
    }

    recordAnnotationAction({
      ...current,
      textsByPage: Object.fromEntries(
        Object.entries(current.textsByPage).map(([page, annotations]) => [
          page,
          annotations.map((annotation) => annotation.id === selectedTextId
            ? { ...annotation, color }
            : annotation),
        ]),
      ),
    })
  }, [recordAnnotationAction, selectedTextId])

  const recolorSelectedStroke = useCallback((color: string) => {
    if (!selectedStrokeId) {
      return
    }

    const current = annotationStateRef.current
    const selectedStroke = Object.values(current.strokesByPage).flat().find((stroke) => stroke.id === selectedStrokeId)
    if (!selectedStroke || selectedStroke.color === color) {
      return
    }

    recordAnnotationAction({
      ...current,
      strokesByPage: Object.fromEntries(
        Object.entries(current.strokesByPage).map(([page, annotations]) => [
          page,
          annotations.map((stroke) => stroke.id === selectedStrokeId
            ? { ...stroke, color }
            : stroke),
        ]),
      ),
    })
  }, [recordAnnotationAction, selectedStrokeId])

  const alignSelectedText = useCallback((align: FlipbookTextAlignment) => {
    if (!selectedTextId) {
      return
    }

    const current = annotationStateRef.current
    const selectedText = Object.values(current.textsByPage).flat().find((annotation) => annotation.id === selectedTextId)
    if (!selectedText || selectedText.align === align) {
      return
    }

    recordAnnotationAction({
      ...current,
      textsByPage: Object.fromEntries(
        Object.entries(current.textsByPage).map(([page, annotations]) => [
          page,
          annotations.map((annotation) => annotation.id === selectedTextId
            ? { ...annotation, align }
            : annotation),
        ]),
      ),
    })
  }, [recordAnnotationAction, selectedTextId])

  const updateSelectedAnnotationLayer = useCallback((operation: AnnotationLayerOperation) => {
    const current = annotationStateRef.current
    const selectedText = selectedTextId
      ? Object.values(current.textsByPage).flat().find((annotation) => annotation.id === selectedTextId)
      : null
    const selectedStroke = selectedStrokeId
      ? Object.values(current.strokesByPage).flat().find((stroke) => stroke.id === selectedStrokeId)
      : null
    const selected = selectedText ?? selectedStroke
    const selectedKind = selectedText ? 'text' : selectedStroke ? 'stroke' : null

    if (!selected || !selectedKind) {
      return
    }

    const getKey = (kind: 'stroke' | 'text', id: string) => `${kind}:${id}`
    const pageItems = [
      ...(current.strokesByPage[selected.page] ?? []).map((stroke) => ({ id: stroke.id, kind: 'stroke' as const, layer: stroke.layer })),
      ...(current.textsByPage[selected.page] ?? []).map((text) => ({ id: text.id, kind: 'text' as const, layer: text.layer })),
    ].sort((left, right) => left.layer - right.layer)
    const selectedIndex = pageItems.findIndex((item) => getKey(item.kind, item.id) === getKey(selectedKind, selected.id))
    const selectedItem = pageItems[selectedIndex]
    if (!selectedItem) {
      return
    }

    const layerChanges = new Map<string, number>()
    const adjacentItem = operation === 'forward'
      ? pageItems[selectedIndex + 1]
      : operation === 'backward'
        ? pageItems[selectedIndex - 1]
        : null

    if (adjacentItem) {
      layerChanges.set(getKey(selectedItem.kind, selectedItem.id), adjacentItem.layer)
      layerChanges.set(getKey(adjacentItem.kind, adjacentItem.id), selectedItem.layer)
    } else if (operation === 'front') {
      layerChanges.set(getKey(selectedItem.kind, selectedItem.id), Math.max(...pageItems.map((item) => item.layer)) + 1)
    } else if (operation === 'back') {
      layerChanges.set(getKey(selectedItem.kind, selectedItem.id), Math.min(...pageItems.map((item) => item.layer)) - 1)
    } else {
      return
    }

    const next: AnnotationState = {
      ...current,
      strokesByPage: {
        ...current.strokesByPage,
        [selected.page]: (current.strokesByPage[selected.page] ?? []).map((stroke) => ({
          ...stroke,
          layer: layerChanges.get(getKey('stroke', stroke.id)) ?? stroke.layer,
        })),
      },
      textsByPage: {
        ...current.textsByPage,
        [selected.page]: (current.textsByPage[selected.page] ?? []).map((text) => ({
          ...text,
          layer: layerChanges.get(getKey('text', text.id)) ?? text.layer,
        })),
      },
    }
    layerOrderRef.current = Math.max(layerOrderRef.current, ...layerChanges.values())
    recordAnnotationAction(next)
  }, [recordAnnotationAction, selectedStrokeId, selectedTextId])

  const bringSelectedForward = useCallback(() => { updateSelectedAnnotationLayer('forward') }, [updateSelectedAnnotationLayer])
  const bringSelectedToFront = useCallback(() => { updateSelectedAnnotationLayer('front') }, [updateSelectedAnnotationLayer])
  const sendSelectedBackward = useCallback(() => { updateSelectedAnnotationLayer('backward') }, [updateSelectedAnnotationLayer])
  const sendSelectedToBack = useCallback(() => { updateSelectedAnnotationLayer('back') }, [updateSelectedAnnotationLayer])

  const cancelText = (id: string) => {
    cancelTextGesture()
    clearTextDraft(id)
    const current = annotationStateRef.current
    const edit = textEditRef.current
    if (edit?.id === id && !edit.isNew) {
      replaceAnnotationState(edit.initialState)
      textEditRef.current = null
      setActiveTextId((current) => current === id ? null : current)
      setSelectedTextId(id)
      return
    }

    const next: AnnotationState = {
      ...current,
      textsByPage: Object.fromEntries(
        Object.entries(current.textsByPage).map(([page, annotations]) => [
          page,
          annotations.filter((annotation) => annotation.id !== id),
        ]),
      ),
    }
    replaceAnnotationState(next)
    if (edit?.id === id && edit.isNew) {
      setHistory((items) => items.slice(0, -1))
      setRedoHistory([])
    }
    textEditRef.current = null
    setActiveTextId((current) => current === id ? null : current)
    setSelectedTextId(null)
  }

  const eraseText = useCallback((id: string) => {
    const current = annotationStateRef.current
    const hasTarget = Object.values(current.textsByPage).some((annotations) => annotations.some((annotation) => annotation.id === id))
    if (!hasTarget) {
      return
    }

    const next: AnnotationState = {
      ...current,
      textsByPage: Object.fromEntries(
        Object.entries(current.textsByPage).map(([page, annotations]) => [
          page,
          annotations.filter((annotation) => annotation.id !== id),
        ]),
      ),
    }
    recordAnnotationAction(next)
    resetTextDrafts()
    cancelTextGesture()
    textEditRef.current = null
    setActiveTextId(null)
    setSelectedTextId(null)
    setSelectedStrokeId(null)
  }, [cancelTextGesture, recordAnnotationAction, resetTextDrafts])

  const deleteSelectedAnnotation = useCallback(() => {
    if (selectedTextId) {
      eraseText(selectedTextId)
      return
    }

    if (!selectedStrokeId) {
      return
    }

    const current = annotationStateRef.current
    recordAnnotationAction({
      ...current,
      strokesByPage: Object.fromEntries(
        Object.entries(current.strokesByPage).map(([page, annotations]) => [
          page,
          annotations.filter((stroke) => stroke.id !== selectedStrokeId),
        ]),
      ),
    })
    setSelectedStrokeId(null)
  }, [eraseText, recordAnnotationAction, selectedStrokeId, selectedTextId])

  const eraseStroke = (event: PointerEvent<SVGPolylineElement>, stroke: AnnotationStroke) => {
    if (activeTool !== 'eraser') {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    const current = annotationStateRef.current
    recordAnnotationAction({
      ...current,
      strokesByPage: {
        ...current.strokesByPage,
        [stroke.page]: (current.strokesByPage[stroke.page] ?? []).filter((item) => item.id !== stroke.id),
      },
    })
    setActiveTextId(null)
    setSelectedStrokeId(null)
  }

  const handleStrokePointerDown = (event: PointerEvent<SVGPolylineElement>, stroke: AnnotationStroke) => {
    if (activeTool !== 'none') {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    textEditRef.current = null
    setActiveTextId(null)
    setSelectedTextId(null)
    setSelectedStrokeId(stroke.id)
  }

  const editText = (annotation: TextAnnotation) => {
    if (disabled || activeTextId === annotation.id) return
    setSelectedStrokeId(null)
    setSelectedTextId(annotation.id)
    if (textEditRef.current?.id !== annotation.id) {
      textEditRef.current = { id: annotation.id, initialState: annotationStateRef.current, isNew: false }
    }
    setActiveTextId(annotation.id)
  }

  const beginTextEdit = (event: PointerEvent<HTMLTextAreaElement>, annotation: TextAnnotation) => {
    if (disabled || event.button !== 0 || !event.isPrimary) {
      return
    }

    event.stopPropagation()
    if (activeTool === 'eraser') {
      event.preventDefault()
      eraseText(annotation.id)
      return
    }

    // Editor content is never a drag surface; preserve native caret/selection/IME.
    editText(annotation)
  }

  const beginTextGesture = (event: PointerEvent<HTMLButtonElement>) => {
    const annotation = Object.values(annotationStateRef.current.textsByPage).flat().find((item) => item.id === event.currentTarget.dataset.textId)
    const gesture = event.currentTarget.dataset.gesture as TextFrameGesture
    if (!annotation) return
    if (disabled || event.button !== 0 || !event.isPrimary || composingTextRef.current) return
    event.preventDefault()
    event.stopPropagation()
    if (activeTextId === annotation.id) commitText(annotation.id)
    const layout = measureTextLayout(annotation)
    const bounds = getAnnotationPage(event.currentTarget)?.getBoundingClientRect()
    if (!layout || !bounds) return
    setSelectedTextId(annotation.id)
    setSelectedStrokeId(null)
    event.currentTarget.setPointerCapture(event.pointerId)
    getFlipbookPerformanceDiagnostics().markPointerDown('text')
    setDraggingTextId(annotation.id)
    textDragRef.current = {
      id: annotation.id,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startRenderedWidth: bounds.width,
      startRenderedHeight: bounds.height,
      initialFrame: layout.frame,
      initialState: annotationStateRef.current,
      gesture,
      target: event.currentTarget,
      layout,
      moved: false,
    }
  }

  const continueTextGesture = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = textDragRef.current
    if (!drag || drag.pointerId !== event.pointerId) {
      return
    }

    const page = getAnnotationPage(event.currentTarget)
    const pageBounds = page?.getBoundingClientRect()
    if (!pageBounds?.width || !pageBounds.height || disabled) {
      cancelTextGesture()
      return
    }
    if (page!.clientWidth !== drag.layout.pageWidth || page!.clientHeight !== drag.layout.pageHeight
      || Math.abs(pageBounds.width - drag.startRenderedWidth) > 1 || Math.abs(pageBounds.height - drag.startRenderedHeight) > 1) {
      cancelTextGesture()
      return
    }

    const deltaX = (event.clientX - drag.startClientX) / pageBounds.width
    const deltaY = (event.clientY - drag.startClientY) / pageBounds.height
    if (!drag.moved && Math.hypot(event.clientX - drag.startClientX, event.clientY - drag.startClientY) < TEXT_DRAG_THRESHOLD_PX) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    getFlipbookPerformanceDiagnostics().markPointerMove('text')
    drag.moved = true
    const currentAnnotation = Object.values(annotationStateRef.current.textsByPage)
      .flat()
      .find((annotation) => annotation.id === drag.id)
    if (!currentAnnotation) {
      return
    }

    const proposed = getTextGestureFrame(drag.initialFrame, drag.gesture, deltaX, deltaY, page!.clientWidth)
    const layout = measureTextLayout(currentAnnotation, proposed)
    if (!layout) return
    drag.layout = layout
    setTextGesturePreview({ id: drag.id, layout })
  }

  const finishTextGesture = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = textDragRef.current
    if (!drag || drag.pointerId !== event.pointerId) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    const page = getAnnotationPage(event.currentTarget)
    const bounds = page?.getBoundingClientRect()
    if (disabled || !page?.isConnected || !bounds || page.clientWidth !== drag.layout.pageWidth || page.clientHeight !== drag.layout.pageHeight
      || Math.abs(bounds.width - drag.startRenderedWidth) > 1 || Math.abs(bounds.height - drag.startRenderedHeight) > 1) {
      cancelTextGesture()
      return
    }
    if (drag.moved) {
      const annotation = Object.values(annotationStateRef.current.textsByPage).flat().find((item) => item.id === drag.id)
      const latestLayout = annotation ? measureTextLayout(annotation, drag.layout.frame) : null
      if (latestLayout) commitTextFrame(drag.id, latestLayout, drag.initialState)
    }
    cancelTextGesture()
  }

  const commitTextFrame = (id: string, layout: TextLayout, previous = annotationStateRef.current) => {
    const current = annotationStateRef.current
    const annotation = Object.values(current.textsByPage).flat().find((item) => item.id === id)
    if (!annotation) return
    const text = textDraftsRef.current[id] ?? annotation.text
    if (!layout.fits || text.length > FLIPBOOK_TEXT_MAX_LENGTH || !text.trim()) {
      setTextNotices((current) => ({ ...current, [id]: text.length > FLIPBOOK_TEXT_MAX_LENGTH
        ? 'Tối đa 240 ký tự. Chưa lưu thay đổi; hãy rút ngắn nội dung.'
        : 'Chữ chưa vừa trang, chưa lưu thay đổi. Kéo rộng khung hoặc rút ngắn nội dung.' }))
      return
    }
    const updated = { ...annotation, ...layout.frame, text }
    if (JSON.stringify(updated) !== JSON.stringify(annotation)) {
      recordAnnotationAction({
        ...current,
        textsByPage: { ...current.textsByPage, [annotation.page]: current.textsByPage[annotation.page].map((item) => item.id === id ? updated : item) },
      }, previous)
    }
    textEditRef.current = null
    clearTextDraft(id)
    setActiveTextId(null)
  }

  const handleLostTextPointerCapture = (event: PointerEvent<HTMLButtonElement>) => {
    if (textDragRef.current?.target === event.currentTarget && textDragRef.current.pointerId === event.pointerId) cancelTextGesture()
  }

  const handleTextEditorKey = (event: ReactKeyboardEvent<HTMLTextAreaElement>, annotation: TextAnnotation) => {
    event.stopPropagation()
    if (event.key !== 'Escape' || event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return
    event.preventDefault()
    if (textDragRef.current) {
      cancelTextGesture()
    } else if (activeTextId === annotation.id && textEditRef.current?.id === annotation.id) {
      cancelText(annotation.id)
    } else {
      setActiveTextId(null)
      setSelectedTextId(null)
    }
  }

  const handleTextFrameKey = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (event.key === 'Escape') {
      event.preventDefault()
      cancelTextGesture()
      return
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
      event.preventDefault()
      if (event.shiftKey) redoAnnotationAction()
      else undoAnnotationAction()
      return
    }
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') {
      event.preventDefault()
      redoAnnotationAction()
      return
    }
    if (disabled || event.nativeEvent.isComposing || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return
    event.preventDefault()
    const annotation = Object.values(annotationStateRef.current.textsByPage).flat().find((item) => item.id === event.currentTarget.dataset.textId)
    const gesture = event.currentTarget.dataset.gesture as TextFrameGesture
    if (!annotation) return
    const layout = measureTextLayout(annotation)
    if (!layout) return
    const step = event.shiftKey ? 10 : 1
    const dx = event.key === 'ArrowLeft' ? -step / layout.pageWidth : event.key === 'ArrowRight' ? step / layout.pageWidth : 0
    const dy = event.key === 'ArrowUp' ? -step / layout.pageHeight : event.key === 'ArrowDown' ? step / layout.pageHeight : 0
    if (gesture !== 'MOVE' && !dx) return
    if (activeTextId === annotation.id) commitText(annotation.id)
    const next = measureTextLayout(annotation, getTextGestureFrame(layout.frame, gesture, dx, dy, layout.pageWidth))
    if (next) commitTextFrame(annotation.id, next)
  }

  const handleAnnotationPagePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || (!activeTextId && !selectedTextId)) {
      return
    }

    if (activeTextId) {
      commitText(activeTextId)
    }
    textEditRef.current = null
    setActiveTextId(null)
    setSelectedStrokeId(null)
    setSelectedTextId(null)
  }

  const handleAnnotationCanvasPointerDown = (event: PointerEvent<SVGSVGElement>) => {
    if (activeTool !== 'none' || event.target !== event.currentTarget) {
      return
    }

    if (activeTextId) {
      commitText(activeTextId)
    }
    textEditRef.current = null
    setActiveTextId(null)
    setSelectedStrokeId(null)
    setSelectedTextId(null)
  }

  const selectedTextColor = selectedTextId
    ? Object.values(annotationState.textsByPage).flat().find((annotation) => annotation.id === selectedTextId)?.color ?? null
    : null
  const selectedTextAlignment = selectedTextId
    ? Object.values(annotationState.textsByPage).flat().find((annotation) => annotation.id === selectedTextId)?.align ?? DEFAULT_TEXT_ALIGNMENT
    : null
  const selectedStroke = selectedStrokeId
    ? Object.values(annotationState.strokesByPage).flat().find((stroke) => stroke.id === selectedStrokeId) ?? null
    : null

  useEffect(() => {
    onHistoryChange({
      canClear: hasAnnotations(annotationState) && !disabled,
      canRedo: redoHistory.length > 0 && !disabled,
      canUndo: history.length > 0 && !disabled,
      alignSelectedText,
      bringSelectedForward,
      bringSelectedToFront,
      clear: clearAnnotations,
      recolorSelectedText,
      deleteSelectedAnnotation,
      recolorSelectedStroke,
      redo: redoAnnotationAction,
      sendSelectedBackward,
      sendSelectedToBack,
      selectedStrokeColor: selectedStroke?.color ?? null,
      selectedStrokeTool: selectedStroke?.tool ?? null,
      selectedTextAlignment,
      selectedTextColor,
      undo: undoAnnotationAction,
    })
  }, [alignSelectedText, annotationState, bringSelectedForward, bringSelectedToFront, clearAnnotations, deleteSelectedAnnotation, disabled, history.length, onHistoryChange, recolorSelectedText, recolorSelectedStroke, redoAnnotationAction, redoHistory.length, selectedStroke, selectedTextAlignment, selectedTextColor, sendSelectedBackward, sendSelectedToBack, undoAnnotationAction])

  useEffect(() => {
    const handleAnnotationShortcut = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (target?.tagName === 'TEXTAREA' || target?.isContentEditable) {
        return
      }

      // IME composition keys (Telex/VNI, Windows/macOS Vietnamese IME, mobile keyboards)
      // must never trigger annotation shortcuts.
      if (event.isComposing || event.keyCode === 229) {
        return
      }

      const modifier = event.ctrlKey || event.metaKey
      const key = event.key.toLowerCase()
      if (modifier && key === 'z') {
        event.preventDefault()
        if (event.shiftKey) {
          redoAnnotationAction()
        } else {
          undoAnnotationAction()
        }
        return
      }

      if (modifier && key === 'y') {
        event.preventDefault()
        redoAnnotationAction()
        return
      }

      if (modifier && (event.code === 'BracketRight' || event.key === ']' || event.key === '}')) {
        event.preventDefault()
        if (event.shiftKey) {
          bringSelectedToFront()
        } else {
          bringSelectedForward()
        }
        return
      }

      if (modifier && (event.code === 'BracketLeft' || event.key === '[' || event.key === '{')) {
        event.preventDefault()
        if (event.shiftKey) {
          sendSelectedToBack()
        } else {
          sendSelectedBackward()
        }
        return
      }

      if (!modifier && (event.key === 'Delete' || event.key === 'Backspace') && (selectedStrokeId || selectedTextId)) {
        event.preventDefault()
        deleteSelectedAnnotation()
        return
      }

      if (event.key === 'Escape') {
        cancelTextGesture()
        textEditRef.current = null
        setActiveTextId(null)
        setSelectedStrokeId(null)
        setSelectedTextId(null)
      }
    }

    document.addEventListener('keydown', handleAnnotationShortcut)
    return () => { document.removeEventListener('keydown', handleAnnotationShortcut) }
  }, [bringSelectedForward, bringSelectedToFront, cancelTextGesture, deleteSelectedAnnotation, redoAnnotationAction, selectedStrokeId, selectedTextId, sendSelectedBackward, sendSelectedToBack, undoAnnotationAction])

  const renderStroke = (stroke: AnnotationStroke) => (
    <polyline
      className={`pdfFlipbookAnnotationStroke pdfFlipbookAnnotationStroke--${stroke.tool}${selectedStrokeId === stroke.id ? ' isSelected' : ''}`}
      key={stroke.id}
      onPointerDown={(event) => {
        eraseStroke(event, stroke)
        handleStrokePointerDown(event, stroke)
      }}
      points={getStrokePoints(stroke.points)}
      pointerEvents="stroke"
      style={{ stroke: stroke.color }}
    />
  )

  const renderStrokeCanvas = (stroke: AnnotationStroke) => (
    <svg
      aria-hidden="true"
      className="pdfFlipbookAnnotationStrokeCanvas"
      key={stroke.id}
      preserveAspectRatio="none"
      style={{ zIndex: stroke.layer }}
      viewBox="0 0 1 1"
    >
      {renderStroke(stroke)}
    </svg>
  )

  const renderDraftStroke = (stroke: AnnotationStroke) => (
    <polyline
      className={`pdfFlipbookAnnotationStroke pdfFlipbookAnnotationStroke--${stroke.tool}`}
      key={stroke.id}
      points={getStrokePoints(stroke.points)}
      pointerEvents="none"
      ref={(element) => { draftPolylineRef.current = element }}
      style={{ stroke: stroke.color }}
    />
  )

  return (
    <div className="pdfFlipbookAnnotationLayer" ref={layerRef}>
      {visiblePageNumbers.map((page, index) => {
        const pageStrokes = annotationState.strokesByPage[page] ?? []
        const pageTexts = annotationState.textsByPage[page] ?? []
        const draftForPage = draftStroke?.page === page ? draftStroke : null

        return (
          <div
            className={`pdfFlipbookAnnotationPage ${isSpreadView ? (index === 0 ? 'isLeft' : 'isRight') : 'isSingle'}`}
            data-page={page}
            key={page}
            onPointerDown={handleAnnotationPagePointerDown}
          >
            {pageStrokes.map(renderStrokeCanvas)}
            {pageTexts.map((annotation) => {
              const layout = textGesturePreview?.id === annotation.id ? textGesturePreview.layout : textLayouts[annotation.id]
              const frame = layout?.frame ?? annotation
              const selected = selectedTextId === annotation.id
              const overflow = layout?.fits === false
              const notice = textNotices[annotation.id] || (overflow
                ? 'Chữ chưa vừa trang. Kéo rộng khung hoặc rút ngắn nội dung; có thể cuộn để đọc đủ chữ.'
                : layout?.recovered ? 'Khung được đo lại để hiện đủ chữ, chưa lưu. Kéo khung để lưu vị trí mới.' : '')
              const messageId = `text-frame-message-${page}-${annotation.id}`
              const heightPx = frame.height * (layout?.pageHeight ?? 0)
              const topSpace = frame.y * (layout?.pageHeight ?? 0)
              const bottomSpace = (1 - frame.y - frame.height) * (layout?.pageHeight ?? 0)
              return (
              <div
                className={`pdfFlipbookAnnotationText${selected ? ' isSelected' : ''}${activeTextId === annotation.id ? ' isActive isEditing' : ''}${draggingTextId === annotation.id ? ' isDragging' : ''}${overflow ? ' isOverflowing' : ''}`}
                key={annotation.id}
                style={{
                  height: `${frame.height * 100}%`,
                  left: `${frame.x * 100}%`,
                  top: `${frame.y * 100}%`,
                  width: `${frame.width * 100}%`,
                  zIndex: selected ? getHighestLayer(annotationState) + 2 : annotation.layer,
                }}
              >
                <textarea
                  aria-label={`Text annotation on page ${page}`}
                  aria-describedby={selected && notice ? messageId : undefined}
                  aria-invalid={overflow || Boolean(textNotices[annotation.id]) || undefined}
                  className="pdfFlipbookAnnotationTextInput"
                  onBlur={() => { commitText(annotation.id) }}
                  onChange={(event) => { handleTextChange(event, annotation) }}
                  onCompositionStart={() => { beginTextComposition(annotation) }}
                  onCompositionEnd={(event) => { finishTextComposition(event, annotation) }}
                  onFocus={() => { editText(annotation) }}
                  onPaste={(event) => {
                    if (!acceptsTextInsertion(event.currentTarget.value, event.clipboardData.getData('text'), event.currentTarget.selectionStart, event.currentTarget.selectionEnd)) {
                      event.preventDefault()
                      setTextNotices((current) => ({ ...current, [annotation.id]: 'Tối đa 240 ký tự. Nội dung vừa dán chưa được thêm.' }))
                    }
                  }}
                  onKeyDown={(event) => { handleTextEditorKey(event, annotation) }}
                  onPointerDown={(event) => { beginTextEdit(event, annotation) }}
                  placeholder="Nhập nội dung"
                  readOnly={disabled || activeTextId !== annotation.id}
                  ref={(element) => { textRefs.current[annotation.id] = element }}
                  style={{ color: annotation.color, textAlign: annotation.align }}
                  value={textDrafts[annotation.id] ?? annotation.text}
                />
                {selected ? ([
                  { key: 'MOVE', gesture: 'MOVE', label: 'Di chuyển khung chữ' },
                  { key: 'TOP_LEFT', gesture: 'RESIZE_LEFT', label: 'Đổi chiều rộng từ góc trên trái' },
                  { key: 'TOP_RIGHT', gesture: 'RESIZE_RIGHT', label: 'Đổi chiều rộng từ góc trên phải' },
                  { key: 'BOTTOM_LEFT', gesture: 'RESIZE_LEFT', label: 'Đổi chiều rộng từ góc dưới trái' },
                  { key: 'BOTTOM_RIGHT', gesture: 'RESIZE_RIGHT', label: 'Đổi chiều rộng từ góc dưới phải' },
                ] as const).map(({ key, gesture, label }) => (
                  <button
                    aria-label={label}
                    className={`pdfFlipbookTextHandle pdfFlipbookTextHandle--${gesture}${gesture !== 'MOVE' ? ' isCorner' : ''}`}
                    data-corner={gesture !== 'MOVE' ? key : undefined}
                    data-gesture={gesture}
                    data-text-id={annotation.id}
                    disabled={disabled}
                    key={key}
                    onKeyDown={handleTextFrameKey}
                    onLostPointerCapture={handleLostTextPointerCapture}
                    onPointerCancel={(event) => { event.stopPropagation(); cancelTextGesture() }}
                    onPointerDown={beginTextGesture}
                    onPointerMove={continueTextGesture}
                    onPointerUp={finishTextGesture}
                    style={gesture === 'MOVE' ? {
                      left: overflow ? `calc(100% - ${FLIPBOOK_TEXT_HANDLE_SIZE}px)` : `calc(50% - ${FLIPBOOK_TEXT_HANDLE_SIZE / 2}px)`,
                      top: overflow ? 0 : topSpace >= FLIPBOOK_TEXT_HANDLE_SIZE ? -FLIPBOOK_TEXT_HANDLE_SIZE : bottomSpace >= FLIPBOOK_TEXT_HANDLE_SIZE ? heightPx : 0,
                    } : {
                      top: key.startsWith('TOP') ? -FLIPBOOK_TEXT_HANDLE_SIZE / 2 : undefined,
                      bottom: key.startsWith('BOTTOM') ? -FLIPBOOK_TEXT_HANDLE_SIZE / 2 : undefined,
                      left: gesture === 'RESIZE_LEFT' ? -FLIPBOOK_TEXT_HANDLE_SIZE / 2 : undefined,
                      right: gesture === 'RESIZE_RIGHT' ? -FLIPBOOK_TEXT_HANDLE_SIZE / 2 : undefined,
                    }}
                    title={gesture === 'MOVE' ? 'Di chuyển' : 'Đổi chiều rộng'}
                    type="button"
                  >
                    <span aria-hidden="true">{gesture === 'MOVE' ? '✥' : null}</span>
                  </button>
                )) : null}
                {selected && notice ? <p className="pdfFlipbookTextNotice" id={messageId} role="status">{notice}</p> : null}
              </div>
              )
            })}
            <svg
              aria-label={activeTool === 'none' ? undefined : `Draw on page ${page}`}
              className="pdfFlipbookAnnotationCanvas"
              onPointerCancel={(event) => { finishStroke(event, true) }}
              onPointerDown={(event) => {
                handleAnnotationCanvasPointerDown(event)
                beginStroke(event, page)
              }}
              onPointerMove={continueStroke}
              onPointerUp={finishStroke}
              preserveAspectRatio="none"
              role={activeTool === 'none' ? undefined : 'application'}
              viewBox="0 0 1 1"
              style={{
                pointerEvents: disabled || activeTool === 'none' ? 'none' : 'auto',
                zIndex: draftForPage?.layer ?? getHighestLayer(annotationState) + 1,
              }}
            >
              {draftForPage ? renderDraftStroke(draftForPage) : null}
            </svg>
          </div>
        )
      })}
      <AppDialog
        actions={(
          <>
            <LmsButton
              onClick={() => { setIsClearDialogOpen(false) }}
              variant="outline"
            >
              Huỷ
            </LmsButton>
            <LmsButton
              onClick={confirmClearAnnotations}
              variant="destructive"
            >
              Xoá ghi chú
            </LmsButton>
          </>
        )}
        description="Thao tác này sẽ xoá tất cả ghi chú trên bề mặt sách hiện tại."
        onClose={() => { setIsClearDialogOpen(false) }}
        open={isClearDialogOpen}
        title="Xoá tất cả ghi chú?"
      />
    </div>
  )
}
